import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useProfile } from "../../contexts/ProfileContext";
import {
  uploadProfileImage,
  deleteProfileImage,
} from "../../firebase/profileService";
import { useUpdateProfileMutation } from "../../hooks/useProfileQuery";
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const ProfileSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("Full name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name is too long"),
  title: Yup.string()
    .required("Professional title is required")
    .max(100, "Title is too long"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  phone: Yup.string()
    .required("Phone number is required")
    .matches(
      /^(\+\d{1,3}[ -]?)?\(?\d{3}\)?[ -]?\d{3}[ -]?\d{4}$/,
      "Please enter a valid phone number"
    ),
  location: Yup.string()
    .required("Location is required")
    .max(100, "Location is too long"),
  shortBio: Yup.string()
    .max(250, "Short bio must be 250 characters or less")
    .required("Short bio is required"),
  bio: Yup.string()
    .required("Bio is required")
    .min(
      50,
      "Your bio should be at least 50 characters to properly introduce yourself"
    ),
  skills: Yup.array().of(
    Yup.object().shape({
      category: Yup.string().required("Category is required"),
      subcategories: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required("Subcategory name is required"),
          skills: Yup.array().of(Yup.string().required("Skill is required")),
        })
      ),
    })
  ),
  socialLinks: Yup.object().shape({
    linkedin: Yup.string().url("Please enter a valid LinkedIn URL").nullable(),
    github: Yup.string().url("Please enter a valid GitHub URL").nullable(),
    twitter: Yup.string().url("Please enter a valid Twitter URL").nullable(),
    website: Yup.string().url("Please enter a valid website URL").nullable(),
    googleScholar: Yup.string()
      .url("Please enter a valid Google Scholar URL")
      .nullable(),
    researchGate: Yup.string()
      .url("Please enter a valid ResearchGate URL")
      .nullable(),
    orcid: Yup.string().url("Please enter a valid ORCID URL").nullable(),
  }),
  approachItems: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
    })
  ),
  researchInterests: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
    })
  ),
  teachingExperience: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
    })
  ),
  softSkills: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
    })
  ),
  industryKnowledge: Yup.array().of(
    Yup.string().required("Knowledge item is required")
  ),
  certifications: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Certification name is required"),
      issuer: Yup.string().required("Issuer is required"),
      date: Yup.string().required("Date is required"),
      url: Yup.string().url("Please enter a valid URL").nullable(),
    })
  ),
  languages: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Language name is required"),
      proficiency: Yup.string().required("Proficiency level is required"),
    })
  ),
  personalStatement: Yup.string().required("Personal statement is required"),
  researchSummary: Yup.string().required("Research summary is required"),
  mentorshipExperience: Yup.string(),
  resumeSummary: Yup.string()
    .required("Resume summary is required")
    .min(50, "Resume summary should be at least 50 characters"),
  skillsDescription: Yup.string().required("Skills description is required"),
  projectsDescription: Yup.string().required(
    "Projects description is required"
  ),
});

const ProfileEditor: React.FC = () => {
  const { profile, loading, refreshProfile } = useProfile();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previewBio, setPreviewBio] = useState<boolean>(false);
  const [bioText, setBioText] = useState<string>("");
  const bioInputRef = useRef<HTMLDivElement>(null);
  const bioPreviewRef = useRef<HTMLDivElement>(null);

  // Use React Query mutation hook for API calls
  const updateProfileMutation = useUpdateProfileMutation();

  // Helper function to convert text to formatted HTML
  const formatBioForPreview = (text: string) => {
    if (!text) return "";

    // Convert URLs to clickable links
    let formatted = text.replace(
      /(https?:\/\/[^\s]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:underline">$1</a>'
    );

    // Convert markdown links
    formatted = formatted.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:underline">$1</a>'
    );

    // Process each paragraph
    const paragraphs = formatted.split("\n\n");
    const processedParagraphs = paragraphs.map((paragraph) => {
      // Process headings (lines starting with #)
      if (paragraph.trim().startsWith("#")) {
        return `<h3 class="text-xl font-semibold my-3">${paragraph
          .trim()
          .substring(1)
          .trim()}</h3>`;
      }

      // Process dividers
      if (paragraph.trim() === "---") {
        return '<hr class="my-4 border-t border-gray-200 dark:border-gray-700">';
      }

      // Process bullet points - either • or *
      if (paragraph.includes("\n• ") || paragraph.includes("\n* ")) {
        const lines = paragraph.split("\n");
        let inList = false;
        let result = "";

        for (const line of lines) {
          if (line.trim().startsWith("• ") || line.trim().startsWith("* ")) {
            if (!inList) {
              result += '<ul class="list-disc pl-5 my-2 space-y-1">';
              inList = true;
            }
            result += `<li>${line.trim().substring(2)}</li>`;
          } else {
            if (inList) {
              result += "</ul>";
              inList = false;
            }
            result += line + "<br>";
          }
        }

        if (inList) {
          result += "</ul>";
        }

        return result;
      }

      // For regular paragraphs, preserve line breaks
      return `<p class="mb-4">${paragraph.replace(/\n/g, "<br>")}</p>`;
    });

    return processedParagraphs.join("");
  };

  useEffect(() => {
    if (profile?.photoURL) {
      setImagePreview(profile.photoURL);
    }

    if (profile?.bio) {
      setBioText(profile.bio);
    }
  }, [profile]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;

    try {
      setIsUploading(true);
      const imageUrl = await uploadProfileImage(imageFile, setUploadProgress);

      // Update profile with new image URL
      await updateProfileMutation.mutateAsync({ photoURL: imageUrl });

      // Clear the file input and refresh profile
      setImageFile(null);
      await refreshProfile();

      toast.success("Profile image updated successfully!");
    } catch (error) {
      toast.error("Failed to upload image");
      console.error("Error uploading image:", error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = async () => {
    if (
      !profile.photoURL ||
      profile.photoURL.startsWith("https://placehold.co")
    )
      return;

    try {
      // Delete the image from storage
      await deleteProfileImage(profile.photoURL);

      // Update profile with placeholder
      await updateProfileMutation.mutateAsync({
        photoURL: "https://placehold.co/300x300/EEE/31343C",
      });

      // Refresh profile
      await refreshProfile();
      setImagePreview("https://placehold.co/300x300/EEE/31343C");

      toast.success("Profile image removed");
    } catch (error) {
      toast.error("Failed to remove image");
      console.error("Error removing image:", error);
    }
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setIsSaving(true);
      console.log("Submit handler triggered!"); // Debug line to confirm function execution
      console.log("Form values: ", values); // Log the values being submitted

      // Ensure the bio text is saved and add default empty arrays for any undefined collections
      const updatedValues = {
        ...values,
        bio: bioText || values.bio,
        skills: values.skills || [],
        approachItems: values.approachItems || [],
        researchInterests: values.researchInterests || [],
        teachingExperience: values.teachingExperience || [],
        softSkills: values.softSkills || [],
        industryKnowledge: values.industryKnowledge || [],
        certifications: values.certifications || [],
        languages: values.languages || [],
      };

      // Use mutation hook instead of direct function call
      console.log("Calling updateProfileMutation with:", updatedValues); // Log what's being sent to the mutation
      await updateProfileMutation.mutateAsync(updatedValues);
      await refreshProfile();
      toast.success("Profile updated successfully!");

      // Scroll to top after saving
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error("Failed to update profile");
      console.error("Error updating profile:", error);
      if (error instanceof Error) {
        console.error("Error details:", error.message, error.stack);
      }
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold">Profile Editor</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Update your personal and professional information displayed on your
          portfolio. Your profile information creates the first impression for
          visitors to your site.
        </p>
      </div>

      <div className="mb-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <span className="mr-2">Profile Photo</span>
            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
              First Impression
            </span>
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
            A professional headshot or high-quality portrait photo is
            recommended. Ideal dimensions are 500x500 pixels with a clear
            background.
          </p>

          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="relative">
              <img
                src={imagePreview || profile.photoURL}
                alt="Profile"
                className="w-40 h-40 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
              />
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                  <div className="text-white text-sm font-medium">
                    {Math.round(uploadProgress)}%
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 w-full sm:w-auto">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="profile-image"
              />
              <label
                htmlFor="profile-image"
                className="btn-outline text-center cursor-pointer w-full sm:w-48"
              >
                Choose Image
              </label>

              <button
                type="button"
                onClick={handleImageUpload}
                disabled={!imageFile || isUploading}
                className={`btn-primary text-center w-full sm:w-48 ${
                  !imageFile || isUploading
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Upload Image
              </button>

              <button
                type="button"
                onClick={handleDeleteImage}
                disabled={
                  isUploading ||
                  profile.photoURL.startsWith("https://placehold.co")
                }
                className={`btn-danger text-center w-full sm:w-48 ${
                  isUploading ||
                  profile.photoURL.startsWith("https://placehold.co")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Remove Image
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <Formik
          initialValues={{
            ...profile,
            skills: profile.skills || [],
            approachItems: profile.approachItems || [],
            researchInterests: profile.researchInterests || [],
            teachingExperience: profile.teachingExperience || [],
            softSkills: profile.softSkills || [],
            industryKnowledge: profile.industryKnowledge || [],
            certifications: profile.certifications || [],
            languages: profile.languages || [],
          }}
          validationSchema={ProfileSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            setFieldValue,
            isValid,
            dirty,
          }) => {
            // Debug validation errors when form is submitted
            console.log("Form validation status:", { isValid, dirty, errors });

            return (
              <Form className="space-y-8">
                {Object.keys(errors).length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md border border-red-200 dark:border-red-800">
                    <p className="text-red-700 dark:text-red-400 font-medium">
                      Please fix the following errors:
                    </p>
                    <ul className="mt-2 list-disc pl-5 space-y-1 text-red-600 dark:text-red-400 text-sm">
                      {Object.entries(errors).map(([field, error]) =>
                        typeof error === "string" ? (
                          <li key={field}>
                            {field}: {error}
                          </li>
                        ) : field === "socialLinks" ? (
                          Object.entries(error as Record<string, string>).map(
                            ([social, err]) => (
                              <li key={`${field}.${social}`}>
                                {social}: {err}
                              </li>
                            )
                          )
                        ) : null
                      )}
                    </ul>
                  </div>
                )}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <span>Basic Information</span>
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="fullName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Full Name*
                      </label>
                      <Field
                        type="text"
                        name="fullName"
                        id="fullName"
                        placeholder="John Doe"
                        className="form-input w-full text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="fullName"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="title"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Professional Title*
                      </label>
                      <Field
                        type="text"
                        name="title"
                        id="title"
                        placeholder="Senior Software Engineer"
                        className="form-input w-full text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Email Address*
                      </label>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        placeholder="name@example.com"
                        className="form-input w-full text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Phone Number*
                      </label>
                      <Field
                        type="text"
                        name="phone"
                        id="phone"
                        placeholder="+1 (555) 123-4567"
                        className="form-input w-full text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="location"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Location*
                      </label>
                      <Field
                        type="text"
                        name="location"
                        id="location"
                        placeholder="San Francisco, CA"
                        className="form-input w-full text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="location"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="shortBio"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Short Bio*{" "}
                        <span className="text-xs text-gray-500">
                          (Appears as tagline)
                        </span>
                      </label>
                      <Field
                        type="text"
                        name="shortBio"
                        id="shortBio"
                        className="form-input w-full text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                        maxLength="250"
                        placeholder="Full-stack developer passionate about creating intuitive user experiences"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Brief introduction that appears in headers</span>
                        <span>{values.shortBio?.length || 0}/250</span>
                      </div>
                      <ErrorMessage
                        name="shortBio"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <span>About Me / Full Bio</span>
                      <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                        Most Important
                      </span>
                    </h2>

                    <button
                      type="button"
                      onClick={() => setPreviewBio(!previewBio)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-primary-600 border border-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:border-primary-400 dark:hover:bg-primary-900/20"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {previewBio ? "Edit Bio" : "Preview Bio"}
                    </button>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-4 text-sm">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-gray-600 dark:text-gray-300">
                        <p className="font-medium mb-1">
                          Tips for writing an effective bio:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Start with your current role and expertise</li>
                          <li>
                            Highlight your most significant professional
                            achievements
                          </li>
                          <li>
                            Include your educational background and
                            certifications
                          </li>
                          <li>
                            Mention your professional philosophy and approach
                          </li>
                          <li>
                            Add personal touches that show your personality
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {!previewBio ? (
                    <div ref={bioInputRef}>
                      <textarea
                        value={bioText}
                        onChange={(e) => {
                          setBioText(e.target.value);
                          setFieldValue("bio", e.target.value);
                        }}
                        className="form-textarea w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring focus:ring-primary-500 focus:ring-opacity-50"
                        style={{
                          height: "250px",
                          marginBottom: "20px",
                          resize: "vertical",
                        }}
                        placeholder="Write your professional bio here. Use blank lines to create paragraphs. Be detailed but concise about your experience, skills, and professional journey."
                      />
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() =>
                            setBioText((text) => text + "\n\n# Heading")
                          }
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          title="Add a heading"
                        >
                          Add Heading
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setBioText((text) => text + "\n\n• List item")
                          }
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          title="Add a bullet point"
                        >
                          Add Bullet Point
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setBioText((text) => text + "\n\n---\n\n")
                          }
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          title="Add a divider"
                        >
                          Add Divider
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setBioText(
                              (text) =>
                                text + " [Link text](https://example.com) "
                            )
                          }
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          title="Add a link"
                        >
                          Add Link
                        </button>
                      </div>
                      <div className="text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 p-3 rounded-md">
                        <p className="font-medium mb-1">Formatting Tips:</p>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Use blank lines between paragraphs</li>
                          <li>Start a line with # for headings</li>
                          <li>Start a line with • or * for bullet points</li>
                          <li>URLs will be automatically converted to links</li>
                        </ul>
                      </div>
                      <ErrorMessage
                        name="bio"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  ) : (
                    <div
                      ref={bioPreviewRef}
                      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 h-64 overflow-y-auto prose dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: formatBioForPreview(bioText),
                      }}
                    />
                  )}
                </div>

                <div className="border-b border-gray-200 dark:border-gray-700 pt-4 pb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">
                      Skills & Expertise
                    </h3>
                    <FieldArray name="skills">
                      {({ push }) => (
                        <button
                          type="button"
                          onClick={() =>
                            push({
                              id: Date.now(),
                              category: "",
                              subcategories: [{ name: "", skills: [""] }],
                            })
                          }
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Category
                        </button>
                      )}
                    </FieldArray>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-4 text-sm">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-600 dark:text-gray-300">
                        Organize your skills hierarchically with categories
                        (e.g., "Mobile App Development"), subcategories (e.g.,
                        "Core Development", "Architecture & Design"), and
                        specific skills within each. This professional
                        organization helps visitors understand your technical
                        expertise depth.
                      </p>
                    </div>
                  </div>

                  <FieldArray name="skills">
                    {({ move, push, remove }) => (
                      <div className="space-y-6">
                        {values.skills.length === 0 ? (
                          <div className="text-center py-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <p className="text-gray-500 dark:text-gray-400">
                              No skill categories added yet.
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                push({
                                  id: Date.now(),
                                  category: "",
                                  subcategories: [{ name: "", skills: [""] }],
                                })
                              }
                              className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                            >
                              <PlusIcon className="h-4 w-4 mr-1" />
                              Add Your First Category
                            </button>
                          </div>
                        ) : (
                          values.skills.map((skill, index) => (
                            <div
                              key={skill.id || index}
                              className="bg-gray-50 dark:bg-gray-700 p-5 rounded-lg relative border border-gray-200 dark:border-gray-600"
                            >
                              <div className="absolute right-3 top-3 flex space-x-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    index > 0 && move(index, index - 1)
                                  }
                                  disabled={index === 0}
                                  className={`p-1 rounded-full ${
                                    index === 0
                                      ? "text-gray-400 cursor-not-allowed"
                                      : "text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600"
                                  }`}
                                  title="Move up"
                                >
                                  <ArrowUpIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    index < values.skills.length - 1 &&
                                    move(index, index + 1)
                                  }
                                  disabled={index === values.skills.length - 1}
                                  className={`p-1 rounded-full ${
                                    index === values.skills.length - 1
                                      ? "text-gray-400 cursor-not-allowed"
                                      : "text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600"
                                  }`}
                                  title="Move down"
                                >
                                  <ArrowDownIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="p-1 rounded-full text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/20"
                                  title="Remove category"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="mb-4">
                                <label
                                  htmlFor={`skills.${index}.category`}
                                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                >
                                  Category Name*
                                </label>
                                <Field
                                  type="text"
                                  name={`skills.${index}.category`}
                                  id={`skills.${index}.category`}
                                  className="form-input w-full max-w-md text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                                  placeholder="e.g., Mobile App Development, Web Development, Data Science"
                                />
                                <ErrorMessage
                                  name={`skills.${index}.category`}
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              {/* Subcategories Section */}
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Subcategories*
                                  </label>
                                  <FieldArray
                                    name={`skills.${index}.subcategories`}
                                  >
                                    {({ push: pushSubcategory }) => (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          pushSubcategory({
                                            name: "",
                                            skills: [""],
                                          })
                                        }
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-primary-600 border border-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:border-primary-400 dark:hover:bg-primary-900/20"
                                      >
                                        <PlusIcon className="h-3 w-3 mr-1" />
                                        Add Subcategory
                                      </button>
                                    )}
                                  </FieldArray>
                                </div>

                                <FieldArray
                                  name={`skills.${index}.subcategories`}
                                >
                                  {({
                                    remove: removeSubcategory,
                                    push: pushSubcategory,
                                  }) => (
                                    <div className="space-y-5 ml-2">
                                      {values.skills[index]?.subcategories?.map(
                                        (subcategory, subcategoryIndex) => (
                                          <div
                                            key={subcategoryIndex}
                                            className="border border-gray-200 dark:border-gray-600 rounded-md p-4 bg-white dark:bg-gray-800"
                                          >
                                            <div className="flex justify-between items-center mb-3">
                                              <div className="flex-grow mr-2">
                                                <label
                                                  htmlFor={`skills.${index}.subcategories.${subcategoryIndex}.name`}
                                                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                                >
                                                  Subcategory Name*
                                                </label>
                                                <Field
                                                  type="text"
                                                  name={`skills.${index}.subcategories.${subcategoryIndex}.name`}
                                                  className="form-input w-full text-gray-800 dark:text-white bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                                                  placeholder="e.g., Core Development, Architecture & Design, Testing"
                                                />
                                                <ErrorMessage
                                                  name={`skills.${index}.subcategories.${subcategoryIndex}.name`}
                                                  component="div"
                                                  className="text-red-500 text-sm mt-1"
                                                />
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  removeSubcategory(
                                                    subcategoryIndex
                                                  )
                                                }
                                                disabled={
                                                  (values.skills[index]
                                                    ?.subcategories?.length ??
                                                    0) <= 1
                                                }
                                                className={`self-start p-1 rounded-full mt-6 ${
                                                  (values.skills[index]
                                                    ?.subcategories?.length ??
                                                    0) <= 1
                                                    ? "text-gray-400 cursor-not-allowed"
                                                    : "text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/20"
                                                }`}
                                                title="Remove subcategory"
                                              >
                                                <TrashIcon className="h-4 w-4" />
                                              </button>
                                            </div>

                                            {/* Skills within Subcategory */}
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Skills in this Subcategory*
                                              </label>
                                              <FieldArray
                                                name={`skills.${index}.subcategories.${subcategoryIndex}.skills`}
                                              >
                                                {({
                                                  push: pushSkill,
                                                  remove: removeSkill,
                                                }) => (
                                                  <div className="space-y-2">
                                                    {values.skills[
                                                      index
                                                    ]?.subcategories?.[
                                                      subcategoryIndex
                                                    ]?.skills.map(
                                                      (item, skillIndex) => (
                                                        <div
                                                          key={skillIndex}
                                                          className="flex gap-2 items-center"
                                                        >
                                                          <Field
                                                            type="text"
                                                            name={`skills.${index}.subcategories.${subcategoryIndex}.skills.${skillIndex}`}
                                                            className="form-input text-gray-800 dark:text-white bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                                                            placeholder={`e.g., Flutter & Dart (3+ years), React, Python`}
                                                          />
                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              removeSkill(
                                                                skillIndex
                                                              )
                                                            }
                                                            disabled={
                                                              (values.skills[
                                                                index
                                                              ]
                                                                ?.subcategories?.[
                                                                subcategoryIndex
                                                              ]?.skills
                                                                ?.length ??
                                                                0) <= 1
                                                            }
                                                            className={`p-1 rounded-full ${
                                                              (values.skills[
                                                                index
                                                              ]
                                                                ?.subcategories?.[
                                                                subcategoryIndex
                                                              ]?.skills
                                                                ?.length ??
                                                                0) <= 1
                                                                ? "text-gray-400 cursor-not-allowed"
                                                                : "text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/20"
                                                            }`}
                                                            title="Remove skill"
                                                          >
                                                            <TrashIcon className="h-4 w-4" />
                                                          </button>
                                                        </div>
                                                      )
                                                    )}
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        pushSkill("")
                                                      }
                                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-primary-600 border border-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:border-primary-400 dark:hover:bg-primary-900/20"
                                                    >
                                                      <PlusIcon className="h-4 w-4 mr-1" />
                                                      Add Skill
                                                    </button>
                                                  </div>
                                                )}
                                              </FieldArray>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Approach Items Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                    My Approach
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add key aspects of your professional approach that appear on
                    your About page.
                  </p>

                  <FieldArray name="approachItems">
                    {({ push, remove, form }) => (
                      <div>
                        {values.approachItems &&
                        values.approachItems.length > 0 ? (
                          <div className="space-y-4 mb-4">
                            {values.approachItems.map(
                              (item: any, index: number) => (
                                <div
                                  key={index}
                                  className="border dark:border-gray-700 rounded-lg p-4 relative"
                                >
                                  <div className="absolute top-2 right-2 flex space-x-1">
                                    {index > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newItems = [
                                            ...values.approachItems,
                                          ];
                                          const temp = newItems[index];
                                          newItems[index] = newItems[index - 1];
                                          newItems[index - 1] = temp;
                                          form.setFieldValue(
                                            "approachItems",
                                            newItems
                                          );
                                        }}
                                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                      >
                                        <ArrowUpIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                    {index <
                                      values.approachItems.length - 1 && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newItems = [
                                            ...values.approachItems,
                                          ];
                                          const temp = newItems[index];
                                          newItems[index] = newItems[index + 1];
                                          newItems[index + 1] = temp;
                                          form.setFieldValue(
                                            "approachItems",
                                            newItems
                                          );
                                        }}
                                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                      >
                                        <ArrowDownIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="p-1 text-red-500 hover:text-red-700"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>

                                  <div className="grid grid-cols-1 gap-4">
                                    <div>
                                      <label
                                        htmlFor={`approachItems.${index}.title`}
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                      >
                                        Title
                                      </label>
                                      <Field
                                        type="text"
                                        name={`approachItems.${index}.title`}
                                        className="input-field text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                                        placeholder="e.g., Clean Architecture"
                                      />
                                      <ErrorMessage
                                        name={`approachItems.${index}.title`}
                                        component="p"
                                        className="mt-1 text-sm text-red-600"
                                      />
                                    </div>

                                    <div>
                                      <label
                                        htmlFor={`approachItems.${index}.description`}
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                      >
                                        Description
                                      </label>
                                      <Field
                                        as="textarea"
                                        name={`approachItems.${index}.description`}
                                        className="input-field text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                                        rows={2}
                                        placeholder="Describe this aspect of your approach..."
                                      />
                                      <ErrorMessage
                                        name={`approachItems.${index}.description`}
                                        component="p"
                                        className="mt-1 text-sm text-red-600"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                            <p className="text-gray-500 dark:text-gray-400">
                              No approach items added yet. Add your first one
                              below.
                            </p>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            push({ id: Date.now(), title: "", description: "" })
                          }
                          className="btn-outline flex items-center mx-auto"
                        >
                          <PlusIcon className="h-5 w-5 mr-1" />
                          Add Approach Item
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Resume Summary Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                    Resume Summary
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    This summary appears at the top of your resume page.
                  </p>

                  <div className="mb-4">
                    <label
                      htmlFor="resumeSummary"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Resume Summary
                    </label>
                    <Field
                      as="textarea"
                      name="resumeSummary"
                      className="input-field text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      rows={5}
                      placeholder="Write a comprehensive summary of your professional background..."
                    />
                    <ErrorMessage
                      name="resumeSummary"
                      component="p"
                      className="mt-1 text-sm text-red-600"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      This should be a comprehensive summary of your skills,
                      experience, and career highlights.
                    </p>
                  </div>
                </div>

                {/* Skills Description Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                    Skills Description
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    This text appears above your skills section on the homepage.
                  </p>

                  <div className="mb-4">
                    <label
                      htmlFor="skillsDescription"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Skills Description
                    </label>
                    <Field
                      as="textarea"
                      name="skillsDescription"
                      className="input-field text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      rows={3}
                      placeholder="Briefly describe your technical expertise and skill areas..."
                    />
                    <ErrorMessage
                      name="skillsDescription"
                      component="p"
                      className="mt-1 text-sm text-red-600"
                    />
                  </div>
                </div>

                {/* Projects Description Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                    Projects Description
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    This text appears above your featured projects section on
                    the homepage.
                  </p>

                  <div className="mb-4">
                    <label
                      htmlFor="projectsDescription"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Projects Description
                    </label>
                    <Field
                      as="textarea"
                      name="projectsDescription"
                      className="input-field text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      rows={3}
                      placeholder="Briefly describe your projects and what visitors can expect to find..."
                    />
                    <ErrorMessage
                      name="projectsDescription"
                      component="p"
                      className="mt-1 text-sm text-red-600"
                    />
                  </div>
                </div>

                {/* Social Links Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                    Social Media Links
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add your social media profiles to connect with others.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="socialLinks.linkedin"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center"
                      >
                        <svg
                          className="w-5 h-5 mr-2 text-[#0077B5]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                        </svg>
                        LinkedIn URL
                      </label>
                      <Field
                        type="url"
                        id="socialLinks.linkedin"
                        name="socialLinks.linkedin"
                        placeholder="https://linkedin.com/in/yourprofile"
                        className="form-input text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="socialLinks.linkedin"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="socialLinks.github"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center"
                      >
                        <svg
                          className="w-5 h-5 mr-2 text-[#333] dark:text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                        </svg>
                        GitHub URL
                      </label>
                      <Field
                        type="url"
                        name="socialLinks.github"
                        id="socialLinks.github"
                        placeholder="https://github.com/yourusername"
                        className="form-input text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="socialLinks.github"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="socialLinks.twitter"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center"
                      >
                        <svg
                          className="w-5 h-5 mr-2 text-[#1DA1F2]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                        </svg>
                        Twitter URL
                      </label>
                      <Field
                        type="url"
                        name="socialLinks.twitter"
                        id="socialLinks.twitter"
                        placeholder="https://twitter.com/yourusername"
                        className="form-input text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                      />
                      <ErrorMessage
                        name="socialLinks.twitter"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="socialLinks.website"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center"
                      >
                        <svg
                          className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
                        </svg>
                        Personal Website URL
                      </label>
                      <Field
                        type="url"
                        name="socialLinks.website"
                        id="socialLinks.website"
                        placeholder="https://yourwebsite.com"
                        className="form-input"
                      />
                      <ErrorMessage
                        name="socialLinks.website"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Social media and professional profiles */}
                <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                  Professional Profiles
                </h3>
                <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="socialLinks.linkedin"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      LinkedIn
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.linkedin"
                      name="socialLinks.linkedin"
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.linkedin"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="socialLinks.github"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      GitHub
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.github"
                      name="socialLinks.github"
                      placeholder="https://github.com/yourusername"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.github"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="socialLinks.twitter"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      Twitter
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.twitter"
                      name="socialLinks.twitter"
                      placeholder="https://twitter.com/yourusername"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.twitter"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="socialLinks.website"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      Personal Website
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.website"
                      name="socialLinks.website"
                      placeholder="https://yourwebsite.com"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.website"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Academic profiles */}
                  <div>
                    <label
                      htmlFor="socialLinks.googleScholar"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      Google Scholar
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.googleScholar"
                      name="socialLinks.googleScholar"
                      placeholder="https://scholar.google.com/citations?user=YOUR_ID"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.googleScholar"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="socialLinks.researchGate"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      ResearchGate
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.researchGate"
                      name="socialLinks.researchGate"
                      placeholder="https://www.researchgate.net/profile/Your_Profile"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.researchGate"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="socialLinks.orcid"
                      className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                      ORCID ID
                    </label>
                    <Field
                      type="url"
                      id="socialLinks.orcid"
                      name="socialLinks.orcid"
                      placeholder="https://orcid.org/0000-0000-0000-0000"
                      className="form-input"
                    />
                    <ErrorMessage
                      name="socialLinks.orcid"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                {/* Personal Statement for Both Audiences */}
                <h3 className="text-xl font-semibold mt-8 mb-4 border-b pb-2">
                  Personal Statement
                </h3>
                <div className="mb-8">
                  <label
                    htmlFor="personalStatement"
                    className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                  >
                    Personal Statement{" "}
                    <span className="text-sm text-gray-500">
                      (A compelling statement about your professional and
                      academic journey)
                    </span>
                  </label>
                  <Field
                    as="textarea"
                    id="personalStatement"
                    name="personalStatement"
                    rows={4}
                    className="form-textarea text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                    placeholder="Share your personal statement that highlights your journey, motivations and aspirations..."
                  />
                  <ErrorMessage
                    name="personalStatement"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Academic Focus Section */}
                <h3 className="text-xl font-semibold mt-8 mb-4 border-b pb-2">
                  Academic Information
                </h3>

                {/* Research Summary */}
                <div className="mb-6">
                  <label
                    htmlFor="researchSummary"
                    className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                  >
                    Research Summary{" "}
                    <span className="text-sm text-gray-500">
                      (Great for academic recruiters)
                    </span>
                  </label>
                  <Field
                    as="textarea"
                    id="researchSummary"
                    name="researchSummary"
                    rows={4}
                    className="form-textarea text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md"
                    placeholder="Describe your research focus, methodologies, and impact..."
                  />
                  <ErrorMessage
                    name="researchSummary"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Research Interests */}
                <div className="mb-6">
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Research Interests{" "}
                    <span className="text-sm text-gray-500">
                      (Key for academic positions)
                    </span>
                  </label>

                  <FieldArray name="researchInterests">
                    {({ remove, push, form }) => (
                      <div className="space-y-4">
                        {form.values.researchInterests.map(
                          (
                            interest: {
                              id: number;
                              title: string;
                              description: string;
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium">
                                  Research Interest #{index + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="mb-2">
                                <label
                                  htmlFor={`researchInterests.${index}.title`}
                                  className="block mb-1 text-sm font-medium"
                                >
                                  Title
                                </label>
                                <Field
                                  name={`researchInterests.${index}.title`}
                                  placeholder="e.g., Machine Learning, NLP, Computer Vision"
                                  className="form-input text-sm"
                                />
                                <ErrorMessage
                                  name={`researchInterests.${index}.title`}
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>

                              <div>
                                <label
                                  htmlFor={`researchInterests.${index}.description`}
                                  className="block mb-1 text-sm font-medium"
                                >
                                  Description
                                </label>
                                <Field
                                  as="textarea"
                                  name={`researchInterests.${index}.description`}
                                  rows={2}
                                  placeholder="Brief description of your research interest and expertise in this area"
                                  className="form-textarea text-sm"
                                />
                                <ErrorMessage
                                  name={`researchInterests.${index}.description`}
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                            </div>
                          )
                        )}

                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                id: Date.now(),
                                title: "",
                                description: "",
                              })
                            }
                            className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-800"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Research Interest
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Teaching Experience */}
                <div className="mb-6">
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Teaching Experience{" "}
                    <span className="text-sm text-gray-500">
                      (Important for academic positions)
                    </span>
                  </label>

                  <FieldArray name="teachingExperience">
                    {({ remove, push, form }) => (
                      <div className="space-y-4">
                        {form.values.teachingExperience.map(
                          (
                            experience: {
                              id: number;
                              title: string;
                              description: string;
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium">
                                  Teaching Experience #{index + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="mb-2">
                                <label
                                  htmlFor={`teachingExperience.${index}.title`}
                                  className="block mb-1 text-sm font-medium"
                                >
                                  Course/Subject
                                </label>
                                <Field
                                  name={`teachingExperience.${index}.title`}
                                  placeholder="e.g., Introduction to Computer Science, Data Structures"
                                  className="form-input text-sm"
                                />
                                <ErrorMessage
                                  name={`teachingExperience.${index}.title`}
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>

                              <div>
                                <label
                                  htmlFor={`teachingExperience.${index}.description`}
                                  className="block mb-1 text-sm font-medium"
                                >
                                  Details
                                </label>
                                <Field
                                  as="textarea"
                                  name={`teachingExperience.${index}.description`}
                                  rows={2}
                                  placeholder="Describe your role, teaching approach, and achievements"
                                  className="form-textarea text-sm"
                                />
                                <ErrorMessage
                                  name={`teachingExperience.${index}.description`}
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                            </div>
                          )
                        )}

                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                id: Date.now(),
                                title: "",
                                description: "",
                              })
                            }
                            className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-800"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Teaching Experience
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Mentorship Experience */}
                <div className="mb-8">
                  <label
                    htmlFor="mentorshipExperience"
                    className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                  >
                    Mentorship Experience{" "}
                    <span className="text-sm text-gray-500">
                      (Valuable for both academic and industry positions)
                    </span>
                  </label>
                  <Field
                    as="textarea"
                    id="mentorshipExperience"
                    name="mentorshipExperience"
                    rows={4}
                    className="form-textarea"
                    placeholder="Describe your experience mentoring students, junior developers, or researchers..."
                  />
                  <ErrorMessage
                    name="mentorshipExperience"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Industry Focus Section */}
                <h3 className="text-xl font-semibold mt-8 mb-4 border-b pb-2">
                  Professional Information
                </h3>

                {/* Soft Skills */}
                <div className="mb-6">
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Soft Skills{" "}
                    <span className="text-sm text-gray-500">
                      (Important for industry recruiters)
                    </span>
                  </label>

                  <FieldArray name="softSkills">
                    {({ remove, push, form }) => (
                      <div className="space-y-4">
                        {form.values.softSkills.map(
                          (
                            skill: {
                              id: number;
                              title: string;
                              description: string;
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium">
                                  Soft Skill #{index + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="mb-2">
                                <label
                                  htmlFor={`softSkills.${index}.title`}
                                  className="block mb-1 text-sm font-medium"
                                >
                                  Skill
                                </label>
                                <Field
                                  name={`softSkills.${index}.title`}
                                  placeholder="e.g., Leadership, Communication, Problem Solving"
                                  className="form-input text-sm"
                                />
                                <ErrorMessage
                                  name={`softSkills.${index}.title`}
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>

                              <div>
                                <label
                                  htmlFor={`softSkills.${index}.description`}
                                  className="block mb-1 text-sm font-medium"
                                >
                                  Description
                                </label>
                                <Field
                                  as="textarea"
                                  name={`softSkills.${index}.description`}
                                  rows={2}
                                  placeholder="Briefly describe how you demonstrate this skill"
                                  className="form-textarea text-sm"
                                />
                                <ErrorMessage
                                  name={`softSkills.${index}.description`}
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                            </div>
                          )
                        )}

                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                id: Date.now(),
                                title: "",
                                description: "",
                              })
                            }
                            className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-800"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Soft Skill
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Industry Knowledge */}
                <div className="mb-6">
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Industry Knowledge{" "}
                    <span className="text-sm text-gray-500">
                      (Important for industry recruiters)
                    </span>
                  </label>

                  <FieldArray name="industryKnowledge">
                    {({ remove, push, form }) => (
                      <div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {form.values.industryKnowledge.map(
                            (knowledge: string, index: number) => (
                              <div
                                key={index}
                                className="bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1 flex items-center"
                              >
                                <span className="text-sm">{knowledge}</span>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="ml-2 text-red-500 hover:text-red-700"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )
                          )}
                        </div>

                        <div className="flex">
                          <Field
                            name="newIndustryKnowledge"
                            placeholder="e.g., Agile Methodology, SaaS, FinTech"
                            className="form-input text-sm mr-2"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newValue = form.values.newIndustryKnowledge;
                              if (newValue && newValue.trim()) {
                                push(newValue.trim());
                                form.setFieldValue("newIndustryKnowledge", "");
                              }
                            }}
                            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Certifications */}
                <div className="mb-8">
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Certifications{" "}
                    <span className="text-sm text-gray-500">
                      (Valuable for industry positions)
                    </span>
                  </label>

                  <FieldArray name="certifications">
                    {({ remove, push, form }) => (
                      <div className="space-y-4">
                        {form.values.certifications.map(
                          (
                            cert: {
                              id: number;
                              name: string;
                              issuer: string;
                              date: string;
                              url?: string;
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium">
                                  Certification #{index + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label
                                    htmlFor={`certifications.${index}.name`}
                                    className="block mb-1 text-sm font-medium"
                                  >
                                    Certification Name
                                  </label>
                                  <Field
                                    name={`certifications.${index}.name`}
                                    placeholder="e.g., AWS Certified Developer"
                                    className="form-input text-sm"
                                  />
                                  <ErrorMessage
                                    name={`certifications.${index}.name`}
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>

                                <div>
                                  <label
                                    htmlFor={`certifications.${index}.issuer`}
                                    className="block mb-1 text-sm font-medium"
                                  >
                                    Issuing Organization
                                  </label>
                                  <Field
                                    name={`certifications.${index}.issuer`}
                                    placeholder="e.g., Amazon Web Services"
                                    className="form-input text-sm"
                                  />
                                  <ErrorMessage
                                    name={`certifications.${index}.issuer`}
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>

                                <div>
                                  <label
                                    htmlFor={`certifications.${index}.date`}
                                    className="block mb-1 text-sm font-medium"
                                  >
                                    Issue Date
                                  </label>
                                  <Field
                                    name={`certifications.${index}.date`}
                                    placeholder="e.g., May 2022"
                                    className="form-input text-sm"
                                  />
                                  <ErrorMessage
                                    name={`certifications.${index}.date`}
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>

                                <div>
                                  <label
                                    htmlFor={`certifications.${index}.url`}
                                    className="block mb-1 text-sm font-medium"
                                  >
                                    Verification URL (Optional)
                                  </label>
                                  <Field
                                    name={`certifications.${index}.url`}
                                    placeholder="https://..."
                                    className="form-input text-sm"
                                  />
                                  <ErrorMessage
                                    name={`certifications.${index}.url`}
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                id: Date.now(),
                                name: "",
                                issuer: "",
                                date: "",
                                url: "",
                              })
                            }
                            className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-800"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Certification
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Languages */}
                <div className="mb-8">
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Languages{" "}
                    <span className="text-sm text-gray-500">
                      (Valuable for both academic and industry positions)
                    </span>
                  </label>

                  <FieldArray name="languages">
                    {({ remove, push, form }) => (
                      <div className="space-y-4">
                        {form.values.languages.map(
                          (
                            language: {
                              id: number;
                              name: string;
                              proficiency: string;
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="flex items-center space-x-4 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg"
                            >
                              <div className="flex-grow">
                                <Field
                                  name={`languages.${index}.name`}
                                  placeholder="Language name"
                                  className="form-input text-sm mb-1"
                                />
                                <ErrorMessage
                                  name={`languages.${index}.name`}
                                  component="div"
                                  className="text-red-500 text-xs"
                                />
                              </div>

                              <div className="flex-grow">
                                <Field
                                  as="select"
                                  name={`languages.${index}.proficiency`}
                                  className="form-select text-sm"
                                >
                                  <option value="">Select proficiency</option>
                                  <option value="Native">Native</option>
                                  <option value="Fluent">Fluent</option>
                                  <option value="Professional">
                                    Professional
                                  </option>
                                  <option value="Intermediate">
                                    Intermediate
                                  </option>
                                  <option value="Basic">Basic</option>
                                </Field>
                                <ErrorMessage
                                  name={`languages.${index}.proficiency`}
                                  component="div"
                                  className="text-red-500 text-xs"
                                />
                              </div>

                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          )
                        )}

                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                id: Date.now(),
                                name: "",
                                proficiency: "",
                              })
                            }
                            className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-800"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Language
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>

                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/admin/dashboard")}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isSaving}
                    className={`btn-primary px-6 ${
                      isSubmitting || isSaving
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isSubmitting || isSaving ? "Saving..." : "Save Profile"}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default ProfileEditor;
